/** Criação de atleta (GDD, seção 4). */
export const STATS = [
  "velocidade",
  "aceleracao",
  "controle",
  "passe",
  "arremesso",
  "rebatida",
  "visao",
  "reflexo",
  "stamina",
  "estabilidade",
] as const;
export type Stat = (typeof STATS)[number];
export type StatDelta = Partial<Record<Stat, number>>;

export type Sex = "homem" | "mulher";
export type Height = "baixo" | "medio" | "alto";
export type Build = "magro" | "medio" | "forte" | "gordo";

export interface BodyOption {
  label: string;
  /** Altura de referência em metros (só para altura). */
  meters?: number;
  /** Ajuste no teto de cada atributo. A soma de cada opção é sempre zero. */
  capDelta: StatDelta;
}

export const SEXES: Readonly<Record<Sex, BodyOption>> = {
  homem: { label: "Homem", capDelta: {} },
  mulher: { label: "Mulher", capDelta: {} },
};

export const HEIGHTS: Readonly<Record<Height, BodyOption>> = {
  baixo: { label: "Baixo", meters: 1.6, capDelta: { aceleracao: 4, reflexo: -4 } },
  medio: { label: "Médio", meters: 1.75, capDelta: {} },
  alto: { label: "Alto", meters: 1.9, capDelta: { reflexo: 4, aceleracao: -4 } },
};

export const BUILDS: Readonly<Record<Build, BodyOption>> = {
  magro: { label: "Magro", capDelta: { velocidade: 4, controle: 3, estabilidade: -7 } },
  medio: { label: "Médio", capDelta: {} },
  forte: { label: "Forte", capDelta: { arremesso: 3, rebatida: 2, stamina: -5 } },
  gordo: { label: "Gordo", capDelta: { estabilidade: 5, reflexo: 3, controle: -8 } },
};

/** Teto base de todo atributo antes dos ajustes de corpo. */
export const BASE_CAP = 90;
export const STAT_MAX = 99;

export interface Body {
  sex: Sex;
  height: Height;
  build: Build;
}

export function statCaps(body: Body): Record<Stat, number> {
  const caps = {} as Record<Stat, number>;
  for (const stat of STATS) {
    const delta =
      (SEXES[body.sex].capDelta[stat] ?? 0) +
      (HEIGHTS[body.height].capDelta[stat] ?? 0) +
      (BUILDS[body.build].capDelta[stat] ?? 0);
    caps[stat] = Math.min(STAT_MAX, BASE_CAP + delta);
  }
  return caps;
}

export function sumDelta(delta: StatDelta): number {
  return Object.values(delta).reduce((n, v) => n + (v ?? 0), 0);
}

/** Nomes dos atributos na interface. */
export const STAT_LABELS: Readonly<Record<Stat, string>> = {
  velocidade: "Velocidade",
  aceleracao: "Aceleração",
  controle: "Controle",
  passe: "Passe",
  arremesso: "Arremesso",
  rebatida: "Rebatida",
  visao: "Visão",
  reflexo: "Reflexo",
  stamina: "Fôlego",
  estabilidade: "Estabilidade",
};
